import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface ParametrosDelivery {
    pedidoMinimo: number
    minimoEntregaGratis: number | null
    valorMinimoRetiradaNoLocal: number | null
    valorMaximoRetiradaNoLocal: number | null
    valorMaximoPagamentoOnline: number | null
    tempoRetiradaLocal: string | null
    delivery: number
    tempoEntregaTexto: string | null
    entregaGratis: boolean
    considerarTaxaEntregaPedidoMinimo: boolean
    permiteRetiradaBalcao: boolean
    permiteFormaPagamentoMesa: boolean
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export interface ParametrosAvancados {
    fusoHorario: number | null
    horaInicioTurnoDia: number | null
    horaInicioTurnoNoite: number | null
    urlWebHookIntegracao: string | null
    authWebHookIntegracao: string | null
    percentualMaximoBebidasPagamentoOnline: number | null
    impedirApenasBebidasPagamentoOnline: boolean
    pontuarNoDelivery: boolean
    permitirBuscaPorAdicionais: boolean
    atualizarEtapaAoAssociarEntregador: boolean
    permiteTaxaEntregaNoturna: boolean
    permiteMinimoEntregaPorBairro: boolean
    enviarMensagemDeAvaliacaoPedidoPelaIzza: boolean
    enviarListaComTodosOsBairrosAtendidos: boolean
    bloquearPagamentoOnlinePrimeiraCompra: boolean
    tocarNovoPedidoSite: boolean
    permitirApenasPagamentoOnline: boolean
    permitirEventoAckSemAceitarPedido: boolean
    exibirConfiguracaoAvancadaProduto: boolean
    possuiPedidoMesa: boolean
}

export interface ParametrosClienteFiel {
    calculoEntrega: number
    tipoRedirecionamento: number
    tipoImpressao: number
    impressaoAutomatica: boolean
    avisoImpressao: string | null
    aceitarPedidoAutomatico: boolean
    tipoPedidoMesa: number
    tipoPontuacao: number
    valorPorSelo: number | null
    tipoPremio: number
    valorPremio: number | null
    valorPremioPercentual: number | null
    valorPremioPercentualMedia: number | null
    valorConsumoMinimoParaSelo: number | null
    fatorMultiplicadorSelos: number
    destinatarioAvaliacao: string | null
    destinatarioFaturas: string | null
    destinatarioPedidos: string | null
    tipoEnvioNovaSenha: number
    taxaEntregaPadrao: number | null
    scriptInicial: string | null
    abaPadraoTelaFidelidade: number
    ocultarConfiguracoesEspecificas: number
    exigirFacebookEndUnicoParaDesconto: boolean
    permitirPagamentoOnlineRetirada: boolean
    exigirValidacaoTelefonePagamentoOnline: boolean
    solicitarComanda: boolean
    somarQtdItens: boolean
    fazerZoomExibicaoFoto: boolean
    adicionarItensNaTelaInicial: boolean
    novaTela: boolean
    recuperacaoCarrinho: boolean
    mostrarTelefoneSite: boolean
    podeSolicitarSaque: boolean
    possuiControleEstoque: boolean
    telaOtimizadaCardapio: boolean
    grupoOpcaoCondicional: boolean
    verificarEnderecoAtendidoPedido: boolean
    recalcularPedido: boolean
    validarGrupoOpcao: boolean
    verificarCardapioAoFinalizarPedido: boolean
    permitirEntregadorAtualizarStatusPedido: boolean
    validarEnderecoListaBloqueio: boolean
    buscaPedidosOtimizada: boolean
    manterEnderecoCliente: boolean
    latitude: number | null
    longitude: number | null
    viasImpressao?: number | null
    colunasPorLinhaImpressao?: number | null
    textoInicioLinha?: string | null
}

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export const obterParametrosDelivery = async function (
    authorization: string
): Promise<ParametrosDelivery | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ParametrosDelivery>>("fornecedor/ObterParametrosDelivery", {
                headers: authHeader(authorization),
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosDelivery>(response, "ObterParametrosDelivery")
    } catch (error) {
        RegistrarLogErro(error, "ObterParametrosDelivery", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarParametrosDelivery = async function (
    authorization: string,
    payload: ParametrosDelivery
): Promise<ParametrosDelivery | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ParametrosDelivery>>(
                "fornecedor/AtualizarParametrosDelivery",
                payload,
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosDelivery>(response, "AtualizarParametrosDelivery")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarParametrosDelivery", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterParametrosAvancados = async function (
    authorization: string
): Promise<ParametrosAvancados | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ParametrosAvancados>>("fornecedor/ObterParametrosAvancados", {
                headers: authHeader(authorization),
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosAvancados>(response, "ObterParametrosAvancados")
    } catch (error) {
        RegistrarLogErro(error, "ObterParametrosAvancados", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarParametrosAvancados = async function (
    authorization: string,
    payload: ParametrosAvancados
): Promise<ParametrosAvancados | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ParametrosAvancados>>(
                "fornecedor/AtualizarParametrosAvancados",
                payload,
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosAvancados>(response, "AtualizarParametrosAvancados")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarParametrosAvancados", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterParametrosClienteFiel = async function (
    authorization: string
): Promise<ParametrosClienteFiel | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ParametrosClienteFiel>>("fornecedor/ObterParametrosClienteFiel", {
                headers: authHeader(authorization),
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosClienteFiel>(response, "ObterParametrosClienteFiel")
    } catch (error) {
        RegistrarLogErro(error, "ObterParametrosClienteFiel", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarParametrosClienteFiel = async function (
    authorization: string,
    payload: ParametrosClienteFiel
): Promise<ParametrosClienteFiel | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ParametrosClienteFiel>>(
                "fornecedor/AtualizarParametrosClienteFiel",
                payload,
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosClienteFiel>(response, "AtualizarParametrosClienteFiel")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarParametrosClienteFiel", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface ParametrosIntegracao {
    // interno = usuário Cliente Fiel (libera cards de acesso restrito: Uber, GetNet)
    interno: boolean
    // iFood
    permiteUtilizarIfood: boolean
    ativaIntegracaoIfoodV2: boolean
    // 99 Food
    shopId99Food: string | null
    // PagSeguro
    tokenLojaPagSeguro: string | null
    // Saipos
    ativaSaipos: boolean
    idPartnerSaipos: string | null
    codStoreSaipos: string | null
    // Eclética
    ativaEcletica: boolean
    idLojaEcletica: string | null
    idRedeEcletica: string | null
    loginTokenEcletica: string | null
    siglaEcletica: string | null
    // Linx/MENEW (MVarandas)
    ativarMvarandas: boolean
    idEstabelecimentoMVarandas: number | null
    // Linked Gourmet
    ativarLinkedGourmet: boolean
    usuarioLinkedGourmet: string | null
    idLojaLinkedGourmet: string | null
    // GetNet (acesso restrito)
    ativaGetnet: boolean
    clientIdGetnet: string | null
    secretIdGetnet: string | null
    sellerIdGetnet: string | null
}

export const obterParametrosIntegracao = async function (
    authorization: string
): Promise<ParametrosIntegracao | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ParametrosIntegracao>>("fornecedor/ObterParametrosIntegracao", {
                headers: authHeader(authorization),
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosIntegracao>(response, "ObterParametrosIntegracao")
    } catch (error) {
        RegistrarLogErro(error, "ObterParametrosIntegracao", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarParametrosIntegracao = async function (
    authorization: string,
    payload: ParametrosIntegracao
): Promise<ParametrosIntegracao | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ParametrosIntegracao>>(
                "fornecedor/AtualizarParametrosIntegracao",
                payload,
                {
                    headers: authHeader(authorization),
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })
        return tratamentoResponse<ParametrosIntegracao>(response, "AtualizarParametrosIntegracao")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarParametrosIntegracao", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
